// hero-media.jsx — full-bleed weather hero: the clip's still photo with the
// clip itself playing over it.
//
// Three layers, bottom up: a plain dark backdrop, the clip's still photo, and
// the video (whose poster is that same still, so the first frame lands on an
// identical picture). Withheld motion — save-data, reduced motion, 2g, low
// battery, "Always off" — drops only the video and keeps the still. Only "no
// clip at all", or a still that will not load, leaves the bare backdrop.
// Same layering as the iOS app's HeroView.
//
// iOS Safari's autoplay rules are strict — it'll mount a muted+playsinline
// video but often won't start it without an explicit .play() call. We use a
// ref and run .load() + .play().catch() in an effect after src lands. If
// play is rejected (e.g. iOS Low Power Mode) we drop the video and let the
// still carry the hero.

import { useEffect, useRef, useState } from 'preact/hooks';
import { resolveClipAsync, resolveStaticClip, loadLibrary } from './clip-resolver.js';

const NO_CLIP = { src: '', still: '', credit: null };

// Matches the iOS backdrop: slate at the crown, ending in the page background
// so the hero runs straight into the cards below.
const plainBackdrop = (brand) => `linear-gradient(180deg, #1e293b 0%, #111827 50%, ${brand.bg} 100%)`;

const sameClip = (a, b) => a.src === b.src && a.still === b.still;

function safeCreditHref(raw) {
  const s = String(raw || '').trim();
  return /^https:\/\//i.test(s) ? s : '';
}

// "Video: Roy Kim · Pexels" — the iOS hero's 9 pt footer credit. `at` picks
// the edge (layouts whose foot is covered by a fade put it at the top) and
// `inset` its distance from that edge.
function HeroCredit({ credit, at, inset }) {
  const author = String(credit.author || '').trim();
  const text = author ? `Video: ${author} · Pexels` : 'Video: Pexels';
  const style = {
    position: 'absolute', right: 16, [at === 'top' ? 'top' : 'bottom']: inset ?? (at === 'top' ? 8 : 6),
    zIndex: 3, maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    fontSize: 9, lineHeight: 1.3, color: 'rgba(255,255,255,.65)',
    textShadow: '0 1px 4px rgba(0,0,0,.5)', textDecoration: 'none',
  };
  const href = safeCreditHref(credit.url);
  if (!href) return <span data-hero-credit style={style}>{text}</span>;
  return (
    <a data-hero-credit href={href} target="_blank" rel="noopener noreferrer"
       aria-label={`Video credit: ${author || 'Pexels'}, Pexels`} style={style}>{text}</a>
  );
}

export function HeroMedia({ state, brand, treatment = 'punch', motion = treatment !== 'poster', bucket = 'midday', brandKey = 'shared', creditAt = 'bottom', creditInset, style = {} }) {
  const key = state === 'partly-cloudy' ? 'cloudy' : state;
  const [clip, setClip] = useState(NO_CLIP);
  const [stillFailed, setStillFailed] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef(null);

  // Resolve even when motion is withheld: the still is the hero then. The
  // previous clip stays on screen until the next one has resolved, and the
  // <img> keeps painting the old photo until the new one has loaded.
  useEffect(() => {
    let cancelled = false;
    loadLibrary()
      .then(() => resolveClipAsync(brandKey, key, bucket))
      // Never leave the hero blank because the dynamic lookup misbehaved —
      // the static wildcard is always a valid answer.
      .catch(() => resolveStaticClip(brandKey, key, bucket))
      .then((next) => {
        if (!cancelled) setClip((prev) => (sameClip(prev, next) ? prev : next));
      });
    return () => { cancelled = true; };
  }, [brandKey, key, bucket]);

  useEffect(() => { setStillFailed(false); }, [clip.still]);
  useEffect(() => { setVideoFailed(false); }, [clip.src, motion]);

  const playing = motion && !!clip.src && !videoFailed;
  const showStill = !!clip.still && !stillFailed;

  // Force iOS Safari to actually start playback. Without this it'll mount the
  // <video> with autoplay attribute but stall in paused state, especially when
  // preload="metadata" or when Low Power Mode is on.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !playing) return;
    v.load();
    const p = v.play();
    if (p && typeof p.then === 'function') {
      p.catch((err) => {
        // Browser blocked autoplay (Low Power, save-data, no gesture, etc.)
        // The still underneath carries the hero.
        console.warn('[m] hero autoplay rejected:', err && err.name);
        setVideoFailed(true);
      });
    }
  }, [clip.src, playing]);

  const surface = playing ? 'video' : showStill ? 'still' : 'plain';

  const scrim = treatment === 'punch'
    ? 'linear-gradient(180deg, rgba(0,0,0,.18) 0%, rgba(0,0,0,.05) 35%, rgba(0,0,0,.55) 100%)'
    : treatment === 'painterly'
      ? `linear-gradient(180deg, ${brand.bg}33, ${brand.bg}aa)`
      : `linear-gradient(180deg, rgba(0,0,0,.25) 0%, rgba(0,0,0,.05) 40%, ${brand.bg}cc 100%)`;
  // One filter for both still and video, so the still is the same picture the
  // video's first frame paints over it.
  const mediaFilter = treatment === 'painterly' ? 'saturate(.7) contrast(.92) brightness(.85)'
                    : treatment === 'subtle'    ? 'saturate(.95) brightness(.78)'
                    : 'saturate(1.05) brightness(.88)';
  const fill = { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: mediaFilter };

  return (
    <div data-hero-surface={surface} style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: plainBackdrop(brand), ...style }}>
      {showStill && (
        <img src={clip.still} alt="" decoding="async" style={fill}
             onError={() => { setStillFailed(true); }}/>
      )}
      {playing && (
        <video
          ref={videoRef}
          src={clip.src}
          poster={showStill ? clip.still : undefined}
          autoPlay muted loop playsInline
          // webkit-playsinline kept for legacy iOS (Safari 9 era PWAs); modern iOS uses playsinline.
          // @ts-ignore — Preact lower-cases the attr so this just shows up as webkit-playsinline.
          webkit-playsinline="true"
          preload="auto"
          disablePictureInPicture
          style={fill}
          onError={() => { setVideoFailed(true); }}
        />
      )}
      <div style={{ position: 'absolute', inset: 0, background: scrim, pointerEvents: 'none' }}/>
      {surface !== 'plain' && clip.credit && <HeroCredit credit={clip.credit} at={creditAt} inset={creditInset}/>}
    </div>
  );
}
